import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertTriangle,
  Ban,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface SuspendTenantModalProps {
  tenantId: string;
  onClose: () => void;
}

export const SuspendTenantModal: React.FC<SuspendTenantModalProps> = ({
  tenantId,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  // Get tenant details to show name and violations
  const { data: tenantData } = useQuery({
    queryKey: ['server-compliance-tenant', tenantId],
    queryFn: async () => {
      const response = await api.serverAdmin.getTenantComplianceDetails(tenantId);
      return response.data;
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async (suspensionReason: string) => {
      return api.serverAdmin.suspendTenantForCompliance(tenantId, suspensionReason);
    },
    onSuccess: () => {
      toast.success('Tenant suspended successfully');
      queryClient.invalidateQueries({ queryKey: ['server-compliance-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['server-compliance-overview'] });
      queryClient.invalidateQueries({ queryKey: ['server-compliance-tenant', tenantId] });
      onClose();
    },
    onError: (error: any) => {
      console.error('Failed to suspend tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to suspend tenant');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }
    suspendMutation.mutate(reason);
  };

  const tenantName = tenantData?.tenant_info?.name || 'Tenant';
  const warnings = tenantData?.metrics_30_days?.warnings || [];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Ban className="h-5 w-5" />
            Suspend Tenant
          </DialogTitle>
          <DialogDescription>
            Suspend {tenantName} for compliance violations
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Critical Violations */}
          {warnings.length > 0 && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Critical Violations Detected:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {warnings.map((warning: string, idx: number) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              Suspension Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason for suspending this tenant (e.g., 'Critical compliance violations: opt-out rate exceeds 10%')"
              rows={6}
              className="resize-none"
              required
            />
            <p className="text-xs text-muted-foreground">
              This reason will be sent to the tenant and recorded in the audit log.
            </p>
          </div>

          {/* Warning */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Suspending this tenant will immediately block their SMS messaging capabilities. 
              They will be notified via email. This action can be reversed by updating the tenant status.
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={suspendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={suspendMutation.isPending || !reason.trim()}
            >
              {suspendMutation.isPending ? 'Suspending...' : 'Suspend Tenant'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

